const STORAGE_KEY = 'basketTopDishAddedPeriod';
const CHANGE_EVENT = 'basketTopDishAddedPeriodChanged';

type TAddedPeriods = Record<string, string>;

export type TAddedDish = {
    dishId: string;
    period: string;
};

const readAddedPeriods = (): TAddedPeriods => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (err) {
        return {};
    }
};

/**
 * @description событие хранилища не приходит в ту же вкладку, где пишет,
 * поэтому изменения рассылаем сами — на них подписан список доп. блюд в корзине
 */
const notifyAboutChange = () => {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

export const getDishAddedPeriod = (dishId: string): string | null => (
    readAddedPeriods()[dishId] || null
);

/**
 * @description все блюда, добавленные с баннера не в текущий цикл
 */
export const getAddedDishes = (): TAddedDish[] => {
    const addedPeriods = readAddedPeriods();

    return Object.keys(addedPeriods)
        .filter((dishId) => Boolean(addedPeriods[dishId]))
        .map((dishId) => ({ dishId, period: addedPeriods[dishId] }));
};

/**
 * @description подписка на изменения списка, снимается в useEffect cleanup
 */
export const subscribeOnAddedDishesChange = (handler: () => void) => {
    window.addEventListener(CHANGE_EVENT, handler);

    return () => window.removeEventListener(CHANGE_EVENT, handler);
};

/**
 * @description сохраняет период, в который блюдо уже добавили в корзину
 */
export const saveDishAddedPeriod = (dishId: string, period: string) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            ...readAddedPeriods(),
            [dishId]: period,
        }));
        notifyAboutChange();
    } catch (err) {
        console.error(err);
    }
};

/**
 * @description блюдо убрали из корзины доп. цикла — выкидываем его и из
 * хранилища, иначе чекаут попробует создать пустой доп. инвойс
 */
export const removeAddedDish = (dishId: string) => {
    try {
        const addedPeriods = readAddedPeriods();

        if (!addedPeriods[dishId]) return;

        const { [dishId]: _, ...rest } = addedPeriods;

        if (Object.keys(rest).length) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }

        notifyAboutChange();
    } catch (err) {
        console.error(err);
    }
};

/**
 * @description период, в который блюдо добавили в обход текущего.
 * Нужен чекауту, чтобы создать доп. инвойс на этот цикл.
 * @note на баннере всегда одно блюдо, поэтому берём первый записанный период
 */
export const getNextPeriodToInvoice = (): string | null => {
    const addedPeriods = readAddedPeriods();

    return Object.keys(addedPeriods)
        .map((dishId) => addedPeriods[dishId])
        .find((period) => Boolean(period)) || null;
};

/**
 * @description чистит период после успешного создания доп. инвойса,
 * чтобы при следующем заказе не создать его повторно
 */
export const clearDishAddedPeriod = (period: string) => {
    try {
        const addedPeriods = readAddedPeriods();
        const rest = Object.keys(addedPeriods).reduce((acc: TAddedPeriods, dishId: string) => {
            if (addedPeriods[dishId] !== period) {
                acc[dishId] = addedPeriods[dishId];
            }
            return acc;
        }, {});

        if (Object.keys(rest).length) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }

        notifyAboutChange();
    } catch (err) {
        console.error(err);
    }
};
